import { jsx as _jsx } from "react/jsx-runtime";
import { ConfirmDialog, formatCount, pluralRu } from '@wit-support/ui';
import { SpendConfirmDialog } from './spend-confirm-dialog.js';
import { dayTotalText } from './spend-summary.js';
import { planBidsText } from './preview-panel.js';
import { ADS_TZ, WILL_BE_CREATED_F, WILL_BE_EXECUTED_M, formatWindowInZone } from '../format.js';
/** Окно старта по моментам запусков — по Лос-Анджелесу. */
function windowText(starts) {
    const sorted = [...starts].sort((a, b) => a.localeCompare(b));
    if (sorted.length === 0)
        return 'сразу после проверки';
    return formatWindowInZone(sorted[0], sorted[sorted.length - 1], ADS_TZ);
}
/** Окно старта пачки: «15.10 03:17–03:50 Лос-Анджелес» или «сразу после проверки». */
export function startWindowText(preview) {
    return windowText(preview.launches.flatMap((l) => (l.startTime != null ? [l.startTime] : [])));
}
/** Опция шаблона «Как создавать»: всё, кроме явного «на паузе», — как в Ads Manager. */
export function isScheduledActive(createMode) {
    return createMode !== 'paused_then_activate';
}
export function createFlowOf(activation, createMode) {
    if (activation === 'manual')
        return 'manual';
    return isScheduledActive(createMode) ? 'scheduled' : 'auto';
}
const FLOW_TAIL = {
    scheduled: 'Как в Ads Manager: кампании создаются включёнными с датой старта и начнут показ в указанное время (время Лос-Анджелеса). Сразу после создания — сверка; ошибка или подмена до старта — пауза до первого показа.',
    auto: 'Всё создаётся на паузе и включится только после чистой сверки.',
    manual: 'Всё создаётся на паузе; включите запуски вручную на карточке пачки.',
};
export const FLOW_TITLE = {
    scheduled: 'Создать запуск?',
    auto: 'Создать и включить?',
    manual: 'Создать на паузе?',
};
/** Подпись кнопки создания: одна и та же на экране и в подтверждении. */
export const CREATE_FLOW_ACTION = {
    scheduled: 'Создать',
    auto: 'Создать и включить',
    manual: 'Создать на паузе',
};
/**
 * Время старта плана считается от момента «Проверить»; при создании auto-ads
 * пересчитывает его от момента создания — старт может сдвинуться на минуту-две
 * (v4 D17). Пересчёт виден в пачке («Что сервис рекламы поменял сам»).
 */
export const START_REFINED_NOTE = 'Точное время старта уточняется при создании: оно может сдвинуться на минуту-другую.';
/**
 * Пояснение «старт уточнится при создании»: решает auto-ads
 * (`startRecomputedAtCreate` + готовая фраза `startNote`); старый auto-ads
 * этого не говорит — тогда общая фраза, если у плана есть старт.
 */
function startNoteText(preview) {
    if (preview.startRecomputedAtCreate === false)
        return '';
    if (preview.startRecomputedAtCreate === true) {
        const note = preview.startNote?.trim() || START_REFINED_NOTE;
        return ` ${note.replace(/\.?$/, '.')}`;
    }
    return preview.launches.some((l) => l.startTime != null) ? ` ${START_REFINED_NOTE}` : '';
}
/** Итог словами для подтверждения (спека §1.1). */
export function confirmText(preview, flow) {
    const accounts = new Set(preview.launches.map((l) => l.platformAccountId)).size;
    const n = preview.totals.campaigns;
    const head = `${pluralRu(n, WILL_BE_CREATED_F)} ${formatCount(n, ['кампания', 'кампании', 'кампаний'])} в ${formatCount(accounts, ['кабинете', 'кабинетах', 'кабинетах'])}, дневной бюджет суммарно ${dayTotalText(preview.spend, preview.totals.dailyBudgetMinorByCurrency)}, старт ${startWindowText(preview)}.`;
    return `${head}${startNoteText(preview)} ${FLOW_TAIL[flow]}`;
}
/** Хвост подтверждения «Выполнить»: режим создания решён в шаблоне, здесь — только включение. */
const ACTIVATION_TAIL = {
    auto: 'Запуски включатся сами — как задано в шаблоне: сразу с датой старта или после чистой сверки.',
    manual: 'Всё создаётся на паузе; включите запуски вручную на карточке пачки.',
};
/**
 * Итог словами для «Выполнить» черновика пачки: те же цифры, что в
 * подтверждении создания (бюджет по валютам, число запусков, окно старта по
 * плану по Лос-Анджелесу, режим включения). Старт по плану, который уже
 * прошёл, auto-ads при выполнении пересчитает — это сказано отдельно.
 */
export function executeConfirmText(batch, now = Date.now()) {
    const launches = batch.launches ?? [];
    const n = batch.counts.total || launches.length;
    const starts = launches.flatMap((l) => (l.startTime != null ? [l.startTime] : []));
    const window = windowText(starts);
    const head = `${pluralRu(n, WILL_BE_EXECUTED_M)} ${formatCount(n, ['запуск', 'запуска', 'запусков'])} в ${formatCount(batch.platformAccountIds.length, ['кабинете', 'кабинетах', 'кабинетах'])}, дневной бюджет суммарно ${dayTotalText(batch.spend, batch.totals.dailyBudgetMinorByCurrency)}, старт по плану ${window}.`;
    const past = starts.some((s) => Date.parse(s) <= now)
        ? ' Старт по плану уже прошёл — время будет пересчитано при выполнении.'
        : '';
    return `${head}${past} ${ACTIVATION_TAIL[batch.activation]}`;
}
/** Зависшим запуск считается после этого (порог auto-ads `DEFAULT_STALE_AFTER_MS`). */
export const BATCH_STUCK_AFTER_MS = 15 * 60_000;
/**
 * Кандидаты на возобновление по запускам пачки — только чтобы решить, показывать
 * ли кнопку, и сказать в подтверждении, что будет. Окончательно решает auto-ads
 * (нечего — 409 `invalid-state` «Нечего возобновлять»).
 */
export function batchResumeCandidates(batch, now = Date.now()) {
    const out = { failed: 0, replan: 0, stuck: 0 };
    for (const l of batch.launches ?? []) {
        if (l.state === 'failed')
            out.failed += 1;
        else if (l.state === 'validated')
            out.replan += 1;
        else if (l.state === 'executing' && now - Date.parse(l.updatedAt) > BATCH_STUCK_AFTER_MS) {
            out.stuck += 1;
        }
        else if (l.state === 'queued' &&
            l.plannedExecuteAt != null &&
            now - Date.parse(l.plannedExecuteAt) > BATCH_STUCK_AFTER_MS) {
            out.stuck += 1;
        }
    }
    return out;
}
/**
 * Хвост «Возобновить»: что будет с включением. `createMode` пачки auto-ads в
 * пачке не отдаёт — берётся из `createDecision` её запусков (решение 84:
 * замораживается при первой записи, resume создаёт остаток так же).
 */
function resumeActivationTail(activation, createMode) {
    if (activation === 'manual')
        return 'Созданное останется на паузе — включите запуски вручную.';
    if (createMode === 'paused_then_activate') {
        return 'Созданное включится само только после чистой сверки.';
    }
    if (createMode === 'scheduled_active') {
        return 'Как в Ads Manager: недосозданное создаётся включённым с датой старта; если до старта слишком мало времени — на паузе, и включать его придётся вручную.';
    }
    return 'Запуски включатся сами — как задано в шаблоне: сразу с датой старта или после чистой сверки.';
}
/** `opt.createMode` пачки по решениям её запусков; null — ни один ещё не создавался. */
export function batchCreateMode(batch) {
    return (batch.launches ?? []).find((l) => l.createDecision != null)?.createDecision?.mode ?? null;
}
/** Итог словами для «Возобновить» пачки. */
export function resumeConfirmText(c, activation, createMode = null) {
    const parts = [
        c.failed > 0 ? `${formatCount(c.failed, ['упавший', 'упавших', 'упавших'])} — повторить` : null,
        c.stuck > 0 ? `${formatCount(c.stuck, ['зависший', 'зависших', 'зависших'])} — поднять` : null,
        c.replan > 0
            ? `${formatCount(c.replan, ['ждущий', 'ждущих', 'ждущих'])} перепланирования — назначить новое время старта`
            : null,
    ].filter((p) => p != null);
    const head = parts.length > 0
        ? `Будут возобновлены запуски: ${parts.join('; ')}.`
        : 'Сервер рекламы проверит пачку и возобновит всё, что можно.';
    return `${head} Повтор продолжает с места остановки — уже созданное в Meta не дублируется. ${resumeActivationTail(activation, createMode)}`;
}
/** Подтверждение «Возобновить» пачки в режиме исполнения (стиль «Выполнить»). */
export function ConfirmResumeBatchDialog({ open, onOpenChange, batch, candidates, onConfirm, }) {
    return (_jsx(ConfirmDialog, { open: open, onOpenChange: onOpenChange, title: "\u0412\u043E\u0437\u043E\u0431\u043D\u043E\u0432\u0438\u0442\u044C \u043F\u0430\u0447\u043A\u0443?", description: resumeConfirmText(candidates, batch.activation, batchCreateMode(batch)), cancelLabel: "\u041E\u0442\u043C\u0435\u043D\u0430", actionLabel: "\u0412\u043E\u0437\u043E\u0431\u043D\u043E\u0432\u0438\u0442\u044C", actionStyle: "primary", onConfirm: onConfirm }));
}
/**
 * Подтверждение «Выполнить» черновика пачки (стиль `ConfirmLaunchDialog`).
 * Новый auto-ads присылает деньги вида пачки (`AdsBatch.spend`, v4 §3.11 п. 4)
 * — тогда `SpendConfirmDialog`: итог крупно и в кнопке, «больше обычного»;
 * прежний текст — ниже целиком. Старый — прежний диалог.
 */
export function ConfirmExecuteBatchDialog({ open, onOpenChange, batch, onConfirm, }) {
    if (batch.spend != null) {
        return (_jsx(SpendConfirmDialog, { open: open, onOpenChange: onOpenChange, title: batch.activation === 'auto' ? 'Выполнить и запустить?' : 'Выполнить на паузе?', lines: batch.spend.lines, fallbackByCurrency: batch.totals.dailyBudgetMinorByCurrency, unusual: batch.spend.unusual, usable: batch.spend.usable, unknownLaunches: batch.spend.unknownLaunches, notBuilt: batch.spend.notBuilt, confirmLabel: "\u0412\u044B\u043F\u043E\u043B\u043D\u0438\u0442\u044C", onConfirm: () => {
                onConfirm();
                onOpenChange(false);
            }, children: executeConfirmText(batch) }));
    }
    return (_jsx(ConfirmDialog, { open: open, onOpenChange: onOpenChange, title: batch.activation === 'auto' ? 'Выполнить и запустить?' : 'Выполнить на паузе?', description: executeConfirmText(batch), cancelLabel: "\u041E\u0442\u043C\u0435\u043D\u0430", actionLabel: "\u0412\u044B\u043F\u043E\u043B\u043D\u0438\u0442\u044C", actionStyle: "primary", onConfirm: onConfirm }));
}
/**
 * Что человек сверяет в подтверждении: итоги и окно старта. Пересчёт плана
 * (другой режим включения) с другими цифрами — повод сказать об этом прямо.
 */
export function confirmSummaryKey(preview) {
    return JSON.stringify({ totals: preview.totals, start: startWindowText(preview) });
}
/** Текст подтверждения черновика: ничего не создаётся и не тратится до «Выполнить». */
export const DRAFT_CONFIRM_TEXT = 'Пачка сохранится черновиком: в Meta ничего не создаётся и не тратится, пока вы не нажмёте «Выполнить» на странице пачки.';
export function ConfirmLaunchDialog({ open, onOpenChange, preview, flow, changed = false, draft = false, note, onConfirm, }) {
    const base = draft ? DRAFT_CONFIRM_TEXT : preview ? confirmText(preview, flow) : '';
    const text = note ? `${base} ${note}` : base;
    return (_jsx(ConfirmDialog, { open: open && preview != null, onOpenChange: onOpenChange, title: draft ? 'Сохранить черновик?' : FLOW_TITLE[flow], description: changed && !draft
            ? `План пересчитан под выбранный режим включения — цифры изменились. ${text}`
            : text, cancelLabel: "\u041E\u0442\u043C\u0435\u043D\u0430", actionLabel: draft ? 'Сохранить черновик' : CREATE_FLOW_ACTION[flow], actionStyle: "primary", onConfirm: onConfirm }));
}
/** Как будут созданы кампании — для строки «Старт» сводки (v4 §11.3). */
const FLOW_STATE = {
    scheduled: 'включёнными',
    auto: 'на паузе, включатся после сверки',
    manual: 'на паузе',
};
/**
 * Сводка «Создать запуск?» (v4 §11.3): что создаётся (кампании, их форма,
 * кабинеты), ставки — когда они разные (лесенка), старт. Числа — из плана
 * сервера.
 */
export function launchConfirmRows(preview, flow) {
    const accounts = new Set(preview.launches.map((l) => l.platformAccountId)).size;
    const shapes = [
        ...new Set(preview.launches.flatMap((l) => (l.structure ? [l.structure.shape] : []))),
    ];
    const rows = [
        {
            label: 'Что создаётся',
            value: `${formatCount(preview.totals.campaigns, ['кампания', 'кампании', 'кампаний'])}${shapes.length > 0 ? ` (${shapes.join(', ')})` : ''} в ${formatCount(accounts, ['кабинете', 'кабинетах', 'кабинетах'])}`,
        },
    ];
    const bids = planBidsText(preview);
    if (bids)
        rows.push({ label: 'Ставки', value: bids });
    rows.push({ label: 'Старт', value: `${startWindowText(preview)} — ${FLOW_STATE[flow]}` });
    return rows;
}
/**
 * «Создать запуск?» на деньгах сервера (v4 §11.0, §11.3): `SpendConfirmDialog`
 * — итог в день первой строкой и в кнопке («Создать · $783.12/день»), сводка,
 * «⚠ Это больше обычного» по `spend.unusual`, прежний текст подтверждения ниже
 * целиком. Кнопка неактивна до ответа (Promise `onConfirm`). Только при
 * `preview.spend` (новый auto-ads); иначе — `ConfirmLaunchDialog`.
 */
export function SpendLaunchConfirmDialog({ open, onOpenChange, preview, flow, changed = false, draft = false, note, onConfirm, }) {
    const base = draft
        ? `${DRAFT_CONFIRM_TEXT} Сумма ниже — сколько будет тратиться в день после выполнения.`
        : confirmText(preview, flow);
    const text = note ? `${base} ${note}` : base;
    return (_jsx(SpendConfirmDialog, { open: open, onOpenChange: onOpenChange, title: draft ? 'Сохранить черновик?' : FLOW_TITLE[flow], lines: preview.spend?.lines ?? [], fallbackByCurrency: preview.totals.dailyBudgetMinorByCurrency, unusual: preview.spend?.unusual ?? null, ...(preview.spend
            ? {
                usable: preview.spend.usable,
                unknownLaunches: preview.spend.unknownLaunches,
                notBuilt: preview.spend.notBuilt,
            }
            : {}), rows: launchConfirmRows(preview, flow), confirmLabel: draft ? 'Сохранить черновик' : CREATE_FLOW_ACTION[flow], onConfirm: onConfirm, children: changed ? `План пересчитан под выбранный режим включения — цифры изменились. ${text}` : text }));
}
//# sourceMappingURL=confirm-launch-dialog.js.map